import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import RemoveIcon from "@mui/icons-material/Remove";
import { Skeleton } from "@mui/material";

import { TimeRange } from "@/shared/domain/dashboards/TimeRange";
import {
  AggregateMetricsRequest,
  NumericAggregation,
  Period,
  PERIOD_NOUN,
} from "@/shared/domain/metrics";
import { useAggregateMetrics } from "@/shared/domain/metrics/hooks";
import type { Condition } from "@/types";

import { getCardTitle } from "../cardTitle";

import { computeTrend, TrendDirection } from "./computeTrend";
import { formatPercent } from "./formatPercent";
import styles from "./TrendTile.module.css";

const DIRECTION_CLASS: Record<TrendDirection, string> = {
  up: styles.up,
  down: styles.down,
  flat: styles.flat,
};

const DIRECTION_ICON: Record<TrendDirection, typeof RemoveIcon> = {
  up: ArrowUpwardIcon,
  down: ArrowDownwardIcon,
  flat: RemoveIcon,
};

export interface TrendTileProps {
  /** Resolved metric this tile shows (controlled by the parent / URL). */
  name: string;
  aggregation: NumericAggregation;
  orgId: string | undefined;
  period: Period;
  range: TimeRange;
  deviceIds?: string[];
  sessionIds?: string[];
  /** Optional: session-property / custom-field predicate applied server-side. */
  condition?: Condition;
}

/**
 * A single-metric KPI card (the placed dashboard's 1×1 "trend" tile): a
 * `aggregation of metric` title, the current-period aggregate value, and its
 * change vs the previous period. Compact by design — typography scales with the tile's container
 * width so it stays legible from one narrow grid cell up to a resized block.
 * Pure display: the metric/aggregation selection is edited through the card's
 * ⋮ → "Edit Data" item, which opens the widget editor on its own route.
 */
export function TrendTile({
  name,
  aggregation,
  orgId,
  period,
  range,
  deviceIds,
  sessionIds,
  condition,
}: TrendTileProps) {
  const request: AggregateMetricsRequest = {
    name,
    period,
    aggregation,
    time_filter: "end_time",
    start_time: range.start_ms,
    end_time: range.end_ms,
    include_device_ids: deviceIds,
    include_session_ids: sessionIds,
    condition,
  };

  const { data, isLoading } = useAggregateMetrics(request, orgId);
  const trend = computeTrend(data?.records);

  const directionClass = DIRECTION_CLASS[trend.direction];
  const DirectionIcon = DIRECTION_ICON[trend.direction];

  // Every bucket in a response carries the same definition-resolved unit, so the
  // first record is representative.
  const unit = data?.records[0]?.unit ?? null;
  const valueText =
    trend.current === null
      ? "—"
      : `${trend.current.toLocaleString(undefined, {
          maximumFractionDigits: 2,
        })}${unit ?? ""}`;

  const titleText = getCardTitle(name, aggregation);

  return (
    <div className={styles.tile}>
      <div className={styles.title} title={titleText}>
        {titleText}
      </div>

      {isLoading ? (
        // Proportional width: the text-variant Skeleton's height already
        // tracks the value's container-scaled font-size, so a percentage
        // width keeps the bar's aspect plausible from a ~110px cell to a
        // ~240px one — a fixed 64px looks stubby beside a 30px line box.
        <Skeleton variant="text" width="50%" className={styles.value} />
      ) : (
        <div className={styles.value}>{valueText}</div>
      )}

      <div className={`${styles.trend} ${directionClass}`}>
        {trend.previous === null ? (
          <span className={styles.changeLabel}>
            No prior {PERIOD_NOUN[period]}
          </span>
        ) : (
          <>
            <DirectionIcon fontSize="inherit" />
            <span>{formatPercent(trend.percentChange)}</span>
            <span className={styles.changeLabel}>
              {/* The period noun (and its leading space) is separately classed
                  so narrow tiles can visually drop just the noun (clip pattern
                  in the CSS) while screen readers still get the full
                  "vs previous week". */}
              vs previous
              <span className={styles.captionNoun}> {PERIOD_NOUN[period]}</span>
            </span>
          </>
        )}
      </div>
    </div>
  );
}
